import React, { useState, useEffect } from 'react';
import FriendlyAlertModal from './FriendlyAlertModal';
import './PublicInspectionView.css';

const PublicInspectionView = ({ serialNumber }) => {
  const [inspectionHistory, setInspectionHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInspectionHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNumber]);

  const normalizeHistoryResponse = (payload) => {
    // รูปแบบจาก Express router: { success: true, data: { ... } }
    if (payload && payload.success && payload.data) {
      return payload.data;
    }

    // รูปแบบจาก server.js: { serial_number, inspection_count, last_inspection_date, inspections }
    if (payload && Array.isArray(payload.inspections)) {
      const inspections = payload.inspections || [];
      const completed = inspections.filter(i => i.status === 'completed').length;
      const pending = inspections.filter(i => i.status === 'pending' || i.status === 'scheduled').length;

      // สร้างข้อมูลสรุปแบบขั้นต่ำจากข้อมูลที่มี
      const inspectionsByRound = {};
      inspections.forEach((i) => {
        const round = i.service_number || 1;
        if (!inspectionsByRound[round]) inspectionsByRound[round] = [];
        inspectionsByRound[round].push(i);
      });

      // เดา sale_info จากข้อมูล inspection ถ้ามี
      const first = inspections[0] || {};
      const saleInfo = {
        customerName: first.customer_name || '-',
        productType: first.product_type || '-',
        saleDate: first.saleDate || null,
        pm_total_required: first.pm_total_required || null,
        pm_interval_months: first.pm_interval_months || null,
      };

      return {
        sale_info: saleInfo,
        serial_number: payload.serial_number || serialNumber,
        total_inspections: payload.inspection_count ?? inspections.length,
        completed_inspections: completed,
        pending_inspections: pending,
        last_inspection: inspections[0] || null,
        inspections_by_round: inspectionsByRound,
        all_inspections: inspections,
      };
    }

    return null;
  };

  const fetchInspectionHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // เรียก API หลักตามซีเรียลของสินค้า
      const response = await fetch(`/api/inspections/history/${serialNumber}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`ไม่พบข้อมูลสำหรับซีเรียล: ${serialNumber}`);
        }
        throw new Error('ไม่สามารถดึงข้อมูลประวัติการตรวจสภาพได้');
      }
      const payload = await response.json();

      const normalized = normalizeHistoryResponse(payload);
      if (!normalized) {
        throw new Error('รูปแบบข้อมูลไม่ถูกต้อง ไม่สามารถแสดงผลได้');
      }

      // ดึงข้อมูลประเภทสินค้าจริงจาก serial number
      await fetchProductTypeBySerial(normalized);

      setInspectionHistory(normalized);
    } catch (error) {
      console.error('Error fetching inspection history:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductTypeBySerial = async (historyData) => {
    try {
      // ตรวจสอบในแบตเตอรี่ก่อน
      const batteryResponse = await fetch(`/api/battery-stock`);
      if (batteryResponse.ok) {
        const batteries = await batteryResponse.json();
        const battery = batteries.find(b => b.serial === serialNumber);
        if (battery) {
          historyData.sale_info.productType = 'battery';
          historyData.sale_info.productDetails = {
            brand: battery.brand,
            capacity: battery.capacity,
            model: battery.model || battery.bmsOrType,
            voltage: battery.voltage
          };
          return;
        }
      }

      // ถ้าไม่พบในแบตเตอรี่ ให้ตรวจสอบในชาร์จเจอร์
      const chargerResponse = await fetch(`/api/charger-stock`);
      if (chargerResponse.ok) {
        const chargers = await chargerResponse.json();
        const charger = chargers.find(c => c.serial === serialNumber);
        if (charger) {
          historyData.sale_info.productType = 'charger';
          historyData.sale_info.productDetails = {
            brand: charger.brand,
            capacity: charger.capacity,
            model: charger.bmsOrType,
            type: charger.bmsOrType
          };
          return;
        }
      }

      // ถ้าไม่พบในทั้งสองที่ ให้ใช้ข้อมูลเดิม
      console.warn('ไม่พบข้อมูลสินค้าสำหรับ serial:', serialNumber);
    } catch (error) {
      console.warn('ไม่สามารถดึงข้อมูลประเภทสินค้าได้:', error);
      // ใช้ข้อมูลเดิมต่อไป
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'รอดำเนินการ',
      'in_progress': 'กำลังดำเนินการ',
      'completed': 'เสร็จสิ้น',
      'cancelled': 'ยกเลิก'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      pending: '#b45309', // amber-700
      in_progress: '#1d4ed8', // blue-700
      completed: '#15803d', // green-700
      cancelled: '#b91c1c' // red-700
    };
    return colorMap[status] || '#374151'; // gray-700
  };

  const getProductTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'battery':
        return 'แบตเตอรี่';
      case 'charger':
        return 'ชาร์จเจอร์';
      case 'mixed':
        return 'แบตเตอรี่ + ชาร์จเจอร์';
      case 'service':
        return 'บริการ';
      case 'other':
        return 'อื่นๆ';
      default:
        // สำหรับกรณีที่เป็นข้อความภาษาไทยแล้ว
        if (type && typeof type === 'string') {
          if (type.includes('แบตเตอรี่') || type.includes('battery')) return 'แบตเตอรี่';
          if (type.includes('ชาร์จเจอร์') || type.includes('ชาร์จ') || type.includes('charger')) return 'ชาร์จเจอร์';
          if (type.includes('ผสม') || type.includes('mixed')) return 'แบตเตอรี่ + ชาร์จเจอร์';
          if (type.includes('บริการ') || type.includes('service')) return 'บริการ';
          return type; // คืนค่าเดิมถ้าไม่ตรงเงื่อนไขใดๆ
        }
        return '-';
    }
  };

  const renderInspectionDetails = (inspection) => {
    return (
      <div className="inspection-details">
        <div className="detail-row">
          <span className="label">วันที่ตรวจสอบ:</span>
          <span className="value">{inspection.formatted_date || formatDate(inspection.inspection_date)}</span>
        </div>
        <div className="detail-row">
          <span className="label">สถานที่:</span>
          <span className="value">{inspection.location || '-'}</span>
        </div>
        <div className="detail-row">
          <span className="label">ทะเบียนรถ/เลขที่รถ:</span>
          <span className="value">{inspection.vehicle_registration || inspection.vehicle_number || '-'}</span>
        </div>
        <div className="detail-row">
          <span className="label">ช่างเทคนิค:</span>
          <span className="value">{inspection.technician_name || '-'}</span>
        </div>
        <div className="detail-row">
          <span className="label">ลายเซ็นลูกค้า:</span>
          <span className="value">{inspection.customer_signature ? 'มีลายเซ็น' : 'ไม่มีลายเซ็น'}</span>
        </div>
        <div className="detail-row">
          <span className="label">สถานะ:</span>
          <span 
            className="value status" 
            style={{ color: getStatusColor(inspection.status) }}
          >
            {getStatusText(inspection.status)}
          </span>
        </div>
      </div>
    );
  };

  const renderTechnicalData = (inspection) => {
    return (
      <div className="technical-data">
        <h4>ข้อมูลทางเทคนิค</h4>
        <div className="detail-row">
          <span className="label">แรงดันรวม (V):</span>
          <span className="value">{inspection.total_volt || '-'}</span>
        </div>
        <div className="detail-row">
          <span className="label">แรงดันส่วนต่าง (V):</span>
          <span className="value">{inspection.v_diff || '-'}</span>
        </div>
        <div className="detail-row">
          <span className="label">รอบการชาร์จ:</span>
          <span className="value">{inspection.cycle_count || '-'}</span>
        </div>
        <div className="detail-row">
          <span className="label">ขันแน่นเทอมินอล:</span>
          <span className="value">{inspection.terminal_tightness ? 'เสร็จสิ้น' : 'ยังไม่ได้ทำ'}</span>
        </div>
      </div>
    );
  };

  const renderMaintenanceData = (inspection) => {
    return (
      <div className="maintenance-data">
        <h4>งานบำรุงรักษา - แบตเตอรี่</h4>
        <div className="detail-row">
          <span className="label">ทำความสะอาดภายนอก:</span>
          <span className="value">{(inspection.cleaning_done || inspection.battery_external_cleaning) ? 'เสร็จสิ้น' : 'ไม่ได้ทำ'}</span>
        </div>
        <div className="detail-row">
          <span className="label">ทำความสะอาดเทอมินอล:</span>
          <span className="value">{(inspection.terminal_cleaning || inspection.battery_terminal_cleaning) ? 'เสร็จสิ้น' : 'ไม่ได้ทำ'}</span>
        </div>
        <div className="detail-row">
          <span className="label">ขันแน่นเทอมินอล (10Nm):</span>
          <span className="value">{(inspection.terminal_tightness || inspection.battery_terminal_tightening) ? 'เสร็จสิ้น' : 'ไม่ได้ทำ'}</span>
        </div>
        <div className="detail-row">
          <span className="label">ตรวจสอบจอแสดงผลคูลอมป์:</span>
          <span className="value">{(inspection.coulomb_check || inspection.battery_coulomb_display) ? 'เสร็จสิ้น' : 'ไม่ได้ทำ'}</span>
        </div>
        
        <h4 className="mt-3">งานบำรุงรักษา - เครื่องชาร์จ</h4>
        <div className="detail-row">
          <span className="label">ชาร์จเข้า (14-15A):</span>
          <span className="value">{(inspection.charging_done || inspection.charger_charging_current) ? 'ปกติ' : 'ไม่ได้ทำ'}</span>
        </div>
        <div className="detail-row">
          <span className="label">ไฟแสดงสถานะ:</span>
          <span className="value">{inspection.charger_status_display ? 'ปกติ' : 'ไม่ได้ทำ'}</span>
        </div>
        <div className="detail-row">
          <span className="label">พัดลมระบายความร้อน:</span>
          <span className="value">{inspection.charger_cooling_fan ? 'ปกติ' : 'ไม่ได้ทำ'}</span>
        </div>
        <div className="detail-row">
          <span className="label">สภาพสายไฟ:</span>
          <span className="value">{inspection.charger_cable_condition ? 'ปกติ' : 'ไม่ได้ทำ'}</span>
        </div>
        
        {inspection.notes && (
          <div className="detail-row mt-3">
            <span className="label">หมายเหตุ:</span>
            <span className="value">{inspection.notes}</span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="public-inspection-view">
        <div className="loading">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-inspection-view">
        <FriendlyAlertModal
          show={true}
          title="ไม่พบข้อมูล"
          icon="🥺"
          message={(
            <div className="thai-text">
              <p className="mb-2">{error}</p>
              <p className="mb-0">กรุณาตรวจสอบหมายเลขซีเรียลอีกครั้ง หรือกลับไปค้นหาใหม่</p>
            </div>
          )}
          actions={[
            { label: 'กลับไปค้นหา', variant: 'primary', onClick: () => { window.location.href = '/'; } },
          ]}
          onClose={() => { window.location.href = '/'; }}
        />
      </div>
    );
  }

  if (!inspectionHistory) {
    return (
      <div className="public-inspection-view">
        <FriendlyAlertModal
          show={true}
          title="ไม่พบข้อมูล"
          icon="🥺"
          message={(
            <div className="thai-text">
              <p className="mb-2">ไม่พบข้อมูลประวัติการตรวจสภาพสำหรับซีเรียลนี้</p>
              <p className="mb-0">กรุณากลับไปค้นหาซีเรียลอื่น</p>
            </div>
          )}
          actions={[
            { label: 'กลับไปค้นหา', variant: 'primary', onClick: () => { window.location.href = '/'; } },
          ]}
          onClose={() => { window.location.href = '/'; }}
        />
      </div>
    );
  }

  const { sale_info, inspections_by_round, total_inspections, completed_inspections, pending_inspections } = inspectionHistory;

  return (
    <div className="public-inspection-view">
      {/* Header */}
      <div className="header">
        <div className="header-top">
          <button 
            className="btn btn-outline-primary back-button"
            onClick={() => window.location.href = '/'}
            title="ย้อนกลับไปหน้าหลัก"
          >
            <i className="fas fa-arrow-left me-2"></i>
            ค้นหาซีเรียลอื่น
          </button>
        </div>
        <h1>รายงานประวัติการตรวจสภาพแบตเตอรี่</h1>
        <div className="company-info">
          <div>บริษัท เค.ทีแพน จำกัด (สำนักงานใหญ่)</div>
          <div>1408 9-10 ถนนพหลโยธิน แขวงจอมพล เขตจตุจักร กรุงเทพมหานคร 10900</div>
        </div>
      </div>

      {/* Summary Information */}
      <div className="summary-section">
        <h2>ข้อมูลสรุป</h2>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">หมายเลขซีเรียล:</span>
            <span className="value">{serialNumber}</span>
          </div>
          <div className="summary-item">
            <span className="label">ลูกค้า:</span>
            <span className="value">{sale_info?.customerName || '-'}</span>
          </div>
          <div className="summary-item">
            <span className="label">วันที่ส่งมอบ:</span>
            <span className="value">{formatDate(sale_info?.delivery_date || sale_info?.saleDate)}</span>
          </div>
          <div className="summary-item">
            <span className="label">ประเภทสินค้า:</span>
            <span className="value">{getProductTypeLabel(sale_info?.productType)}</span>
          </div>
          {sale_info?.productDetails && (
            <div className="summary-item">
              <span className="label">รายละเอียดสินค้า:</span>
              <span className="value">
                {sale_info.productType === 'battery' 
                  ? `${sale_info.productDetails.brand} ${sale_info.productDetails.capacity}Ah${sale_info.productDetails.voltage ? ` (${sale_info.productDetails.voltage}V)` : ''}`
                  : `${sale_info.productDetails.brand} ${sale_info.productDetails.capacity}A${sale_info.productDetails.type ? ` (${sale_info.productDetails.type})` : ''}`
                }
              </span>
            </div>
          )}
          <div className="summary-item">
            <span className="label">จำนวนครั้งที่ต้องตรวจ:</span>
            <span className="value">{sale_info?.pm_total_required || '-'} ครั้ง</span>
          </div>
          <div className="summary-item">
            <span className="label">ช่วงเวลาตรวจ:</span>
            <span className="value">ทุก {sale_info?.pm_interval_months || '-'} เดือน</span>
          </div>
          <div className="summary-item">
            <span className="label">การตรวจสภาพล่าสุด:</span>
            <span className="value">{inspectionHistory.last_inspection ? formatDate(inspectionHistory.last_inspection.inspection_date) : 'ยังไม่มี'}</span>
          </div>
          <div className="summary-item">
            <span className="label">ช่างเทคนิคล่าสุด:</span>
            <span className="value">{inspectionHistory.last_inspection?.technician_name || '-'}</span>
          </div>
        </div>
        
        <div className="statistics">
          <div className="stat-item">
            <span className="stat-number">{total_inspections}</span>
            <span className="stat-label">ครั้งทั้งหมด</span>
          </div>
          <div className="stat-item completed">
            <span className="stat-number">{completed_inspections}</span>
            <span className="stat-label">เสร็จสิ้น</span>
          </div>
          <div className="stat-item pending">
            <span className="stat-number">{pending_inspections}</span>
            <span className="stat-label">รอดำเนินการ</span>
          </div>
        </div>
      </div>

      {/* Inspection History by Round */}
      <div className="history-section">
        <h2>ประวัติการตรวจสภาพแต่ละครั้ง</h2>
        
        {Object.keys(inspections_by_round).length === 0 ? (
          <div className="no-data">ยังไม่มีข้อมูลการตรวจสภาพ</div>
        ) : (
          Object.keys(inspections_by_round)
            .sort((a, b) => parseInt(b) - parseInt(a)) // เรียงจากครั้งล่าสุดไปเก่าสุด
            .map(round => (
              <div key={round} className="inspection-round">
                <h3>การตรวจสภาพครั้งที่ {round}</h3>
                
                {inspections_by_round[round]
                  // เรียงจากเก่าสุด -> ล่าสุด เพื่อให้หมายเลข #1 เริ่มจากรายการแรกของรอบนั้น
                  .sort((a, b) => new Date(a.inspection_date) - new Date(b.inspection_date))
                  .map((inspection, index) => (
                    <div key={inspection.id} className="inspection-card">
                      <div className="inspection-header">
                        <h4>รายการตรวจสอบ #{index + 1}</h4>
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(inspection.status) }}
                        >
                          {getStatusText(inspection.status)}
                        </span>
                      </div>
                      
                      <div className="inspection-content">
                        <div className="section">
                          <h5>รายละเอียดการตรวจสอบ</h5>
                          {renderInspectionDetails(inspection)}
                        </div>
                        
                        <div className="section">
                          {renderTechnicalData(inspection)}
                        </div>
                        
                        <div className="section">
                          {renderMaintenanceData(inspection)}
                        </div>
                      </div>
                    </div>
                  ))
                }
              </div>
            ))
        )}
      </div>
    </div>
  );
}

export default PublicInspectionView;